import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type {
  NativeProfileItemRow,
  PublishedFeedItem,
} from '@/lib/platforms/instagram-feed-mapper';
import { mapNativeProfileRowToPublishedFeedItem } from '@/lib/platforms/instagram-feed-mapper';

const DEFAULT_WINDOW_DAYS = 90;
const MIN_PROFILE_ANCHORS = 12;
const MAX_CACHED_PROFILE_ITEMS = 500;

export interface InstagramFeedSyncState {
  connectionId: string;
  lastSyncedAt: string | null;
  syncedThrough: string | null;
  lastErrorCode: string | null;
}

/**
 * Connection-scoped read model over durable native Instagram profile history.
 * Browser callers send Mora connection UUIDs only. The Edge function resolves
 * the provider account id and token server-side.
 */
class FeedHistoryService extends EnhancedBaseService {
  private static instance: FeedHistoryService;

  public static getInstance(): FeedHistoryService {
    if (!FeedHistoryService.instance) {
      FeedHistoryService.instance = new FeedHistoryService({
        deduplication: { enabled: true, ttl: 5000 },
      });
    }
    return FeedHistoryService.instance;
  }

  /**
   * Cached published Instagram feed tiles for one connected account, newest
   * first, bounded to `windowDays` (default 90 — matches the plan's B1 step
   * 3-5 default window).
   */
  async getPublishedInstagramFeed(
    connectionId: string,
    windowDays: number = DEFAULT_WINDOW_DAYS,
  ): Promise<ServiceResponse<PublishedFeedItem[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const since = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = (await this.client
        .from('native_profile_items')
        .select(
          'connection_id, native_post_id, placement, thumbnail_path, permalink, media_type, published_at, observed_at',
        )
        .eq('connection_id', connectionId)
        .eq('share_to_feed', true)
        .is('tombstoned_at', null)
        .order('published_at', { ascending: false })
        .limit(MAX_CACHED_PROFILE_ITEMS)) as unknown as {
        data: NativeProfileItemRow[] | null;
        error: { message: string } | null;
      };

      if (error) {
        throw this.createError('Failed to load cached Instagram feed', 'QUERY_ERROR', error);
      }

      return (data ?? [])
        .filter((row, index) => row.published_at >= since || index < MIN_PROFILE_ANCHORS)
        .map((row) => {
          const { data: publicUrl } = this.client.storage
            .from('post_media')
            .getPublicUrl(row.thumbnail_path);
          return mapNativeProfileRowToPublishedFeedItem(row, publicUrl.publicUrl);
        });
    }, 'Failed to load cached Instagram feed');
  }

  /**
   * Freshness belongs to the exact connected account, not to the user or the
   * Instagram platform broadly. A missing row means the account has never
   * completed a native profile sync.
   */
  async getInstagramFeedSyncState(
    connectionId: string,
  ): Promise<ServiceResponse<InstagramFeedSyncState | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('native_profile_sync_state')
        .select('connection_id, last_synced_at, synced_through, last_error_code')
        .eq('connection_id', connectionId)
        .maybeSingle();

      if (error) {
        throw this.createError('Failed to load Instagram feed freshness', 'QUERY_ERROR', error);
      }
      if (!data) return null;

      return {
        connectionId: data.connection_id,
        lastSyncedAt: data.last_synced_at,
        syncedThrough: data.synced_through,
        lastErrorCode: data.last_error_code,
      };
    }, 'Failed to load Instagram feed freshness');
  }

  /**
   * Triggers a live Graph API resync for one connected Instagram account and
   * returns the freshly-upserted tiles. The edge fn resolves the account
   * owner server-side from the caller's JWT (mirrors ensureAuthenticatedOwner's
   * current_account_owner_id() resolution, unavailable under the fn's
   * service-role client) — no owner id needs to be sent from the client.
   */
  async refreshInstagramFeed(connectionId: string): Promise<ServiceResponse<PublishedFeedItem[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('fetch-instagram-feed', {
        body: { connectionId },
      });

      if (error) {
        throw this.createError(
          error.message || 'Failed to refresh Instagram feed',
          'FUNCTION_ERROR',
          error,
        );
      }
      if (!data?.success) {
        throw this.createError(
          data?.error || 'Failed to refresh Instagram feed',
          'FUNCTION_ERROR',
          data,
        );
      }

      const items = (data.items ?? []) as Array<
        NativeProfileItemRow & { durable_media_url: string }
      >;
      return items.map((row) => mapNativeProfileRowToPublishedFeedItem(row, row.durable_media_url));
    }, 'Failed to refresh Instagram feed');
  }
}

export const feedHistoryService = FeedHistoryService.getInstance();
export { FeedHistoryService };
